import {useMutation, useQueryClient} from '@tanstack/react-query';
import {trackProductEvent} from '@/matic/lib/product-observability';
import {getMasterDataApi} from '../api/master-data-api';
import {masterDataKeys} from '../query-keys';
import type {ObjectRecord} from '../types/master-data-types';

/**
 * Create an ObjectRecord for a given type. Used by the inline "+ New {Type}"
 * quick-create affordance in the association picker, so a user can mint a
 * customer/part/project on the spot instead of leaving the note/file flow.
 *
 * Takes the field data straight from ``ObjectRecordForm`` — whatever fields the
 * type actually has (``name`` + ``tribal_knowledge`` by default, plus anything
 * else the type owner configured), rather than hardcoding ``name``/``description``.
 *
 * Invalidates the same key set as ``RelayCreateDialog``'s record-create step so
 * every reader refreshes: the single-type records list, the batched
 * records-by-type query the Library LHS object picker reads from, and the
 * object-type lists (a type only surfaces a bucket once it has a record in
 * scope). Without the batch + type-list invalidations, a quick-created record
 * stayed invisible in the Library picker until the 5-minute staleTime elapsed
 * or a manual refresh.
 */
export function useCreateObjectRecord() {
  const api = getMasterDataApi();
  const queryClient = useQueryClient();

  return useMutation<
    ObjectRecord,
    Error,
    {typeId: string; data: Record<string, unknown>}
  >({
    mutationFn: async ({typeId, data}) =>
      api.createObjectRecord(typeId, {data}),
    async onSuccess(record, {typeId}) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: masterDataKeys.objectRecordLists(typeId),
        }),
        queryClient.invalidateQueries({
          queryKey: masterDataKeys.objectRecordsBatchAll(),
        }),
        queryClient.invalidateQueries({
          queryKey: masterDataKeys.objectTypeLists(),
        }),
      ]);
      trackProductEvent('resource.created', {
        resourceType: 'object_record',
        typeId,
        recordId: record.id,
        code: record.code,
        trigger: 'quick_create',
      });
    },
  });
}
